import React, {useEffect} from 'react';
import {Box, Typography} from '@mui/material';
import './ActiveProjectsWidget.scss';
import ArrWidgetWrapper from '../../../common/arr-widget-wrapper/ArrWidgetWrapper';
import {
  useActiveProjectData,
  useChangeRoleToRMU,
} from '../../../../hooks/dashboard/useDashboard';
import {t} from 'i18next';
import {useNavigate} from 'react-router-dom';
import {setAuthenticatedUser} from '../../../../redux/slice/authenticatedUserSlice';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(utc);
dayjs.extend(customParseFormat);

const ActiveProjectsWidget: React.FC<{
  entityId?: number;
  updatedAt?: number;
}> = ({entityId, updatedAt}) => {
  const {data, isLoading, isError, refetch} = useActiveProjectData(
    entityId || 0
  );
  const loginUserData = useSelector(
    (state: RootState) => state.authenticatedUser.user
  );

  const {mutate: changeRoleToRMU} = useChangeRoleToRMU();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Refetch data on updatedAt changes
  useEffect(() => {
    refetch();
  }, [updatedAt]);

  const handleProjectClick = (projectId: number) => {
    changeRoleToRMU(
      {projectId: projectId},
      {
        onSuccess: (result) => {
          const mergedUser = {
            ...loginUserData,
            projectId: result?.projectId,
            securityGroupId: result?.securityGroupId,
            baseRoleId: result?.baseRoleId,
            roleId: result?.roleId,
            roleName: result?.roleName,
            landingPage: result?.landingPage,
          };
          dispatch(setAuthenticatedUser(mergedUser));
          navigate(result?.landingPage);
        },
      }
    );
  };

  const getTimeAgo = (dateString: string): string => {
    if (!dateString) {
      return '';
    }

    const date = dayjs.utc(dateString, 'YYYY/MM/DD HH:mm:ss.SSS').local();
    const now = dayjs();
    const totalMinutes = now.diff(date, 'minute');
    if (totalMinutes <= 0) {
      return t('dashboard.activeProjects.justnow');
    }

    const days = Math.floor(totalMinutes / (24 * 60));
    const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
    const minutes = totalMinutes % 60;

    const parts: string[] = [];
    if (days > 0) {
      parts.push(
        t('dashboard.activeProjects.dayDuration').replace('${days}', `${days}`)
      );
    }
    if (hours > 0) {
      parts.push(
        t('dashboard.activeProjects.hrDuration').replace('${hours}', `${hours}`)
      );
    }
    if (minutes > 0) {
      parts.push(
        t('dashboard.activeProjects.minDuration').replace(
          '${minutes}',
          `${minutes}`
        )
      );
    }
    return t('dashboard.activeProjects.agoDuration').replace(
      '${time}',
      `${parts.join(' ')}`
    );
  };
  return (
    <ArrWidgetWrapper
      title={t('dashboard.activeProjects.title')}
      showInfo
      loading={isLoading}
      error={isError}
      tooltipContent={t('dashboard.activeProjects.daToolTip')}
    >
      <Box className="active-projects-root">
        {/* Top Summary */}
        <Box className="summary-row">
          <Box className="summary-item">
            <Typography
              className="typo-text label2_size"
              data-testid={`active-projects-title`}
            >
              {t('dashboard.activeProjects.title')}
            </Typography>
            <Typography
              className="typo-text display2_size"
              data-testid={`total-active-projects-size`}
            >
              {data?.totalActiveProjects}
            </Typography>
          </Box>
          <Box className="summary-item">
            <Typography
              className="typo-text label2_size"
              data-testid={`active-projects-widget-total-project`}
            >
              {t('dashboard.activeProjects.totalProject')}
            </Typography>
            <Typography
              className="typo-text display2_size"
              data-testid={`total-projects`}
            >
              {data?.totalProjects}
            </Typography>
          </Box>
        </Box>

        {/* Table Header */}
        <Box className="table-header">
          <Typography
            className="typo-text label2_size table-header-data"
            data-testid={`active-projects-widget-project-name`}
          >
            {t('dashboard.activeProjects.projectName')}
          </Typography>
          <Typography
            className="typo-text label2_size table-header-data"
            data-testid={`active-projects-widget-last-activity`}
          >
            {t('dashboard.activeProjects.lastActivity')}
          </Typography>
        </Box>

        {/* Table Rows */}
        <Box>
          {data?.projectDomainDataList?.map((proj, idx) => (
            <Box
              key={proj.projectID}
              className={`table-row ${idx % 2 === 0 ? 'row-alt' : ''}`}
            >
              <Box className="project-link">
                <Typography
                  className="typo-text subhead5_size project-name"
                  data-testid={`${proj.projectID}-name-active`}
                  onClick={() => handleProjectClick(proj.projectID)}
                >
                  {proj.projectName}
                </Typography>
              </Box>
              <Box className="activity-col">
                <Typography
                  className="typo-text body4_size"
                  data-testid={`${proj.projectID}-last-activity`}
                >
                  {proj.lastActivity}
                </Typography>
                <Typography
                  className="typo-text body4_size last-activity-time"
                  data-testid={`${proj.projectID}-last-activity-time`}
                >
                  {getTimeAgo(proj.lasstActivityOccur)}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </ArrWidgetWrapper>
  );
};

export default ActiveProjectsWidget;
